import { addDaysToDateKey } from "../../timezoneDate";
import {
  buildUrl,
  defaultFetchHeaders,
  defaultReadHeaders,
  fetchJson,
  getCsrfToken,
  handlePaymentRequired,
} from "../../../utils";
import { errorMessageFromResponse, jsonFromResponse } from "../../../apiError";
import {
  Address,
  CollectionTaskPredecessor,
  Task,
  TaskStatus,
  TaskType,
  TaskPrecheckResponse,
} from "../../types";
import { Product } from "../../types";
import { normalizeProduct } from "../inventory/api";
import {
  mapTaskStatusToApi,
  mapTaskTypeToApi,
  normalizeCommissionPayoutDetail,
  normalizeCollectionTaskPredecessors,
  normalizeTaskResponse,
} from "./normalize";
import { createMemoryCache } from "../../cache";
import { setCsrfToken } from "../../../user/api";
import { getEffectiveVendViewMode } from "../../effectiveUser";
import { getTaskIdentityKey } from "../../taskContext";
import { toLocalISODate } from "../../utils";
import { withServerTiming } from "../../perfDiagnostics";
import { taskPerfEnabled } from "../../taskPerf";
import { usesRoutableTaskFeed, type TaskListFilters } from "../../taskScopes";
import { ApiResponseError } from "../../../apiError";

export type { TaskListFilters } from "../../taskScopes";

const backend = process.env.REACT_APP_BACKEND_URL || "";
const taskBase = `${backend}/task`;
const MACHINE_TASK_TYPES: TaskType[] = [
  "machine_service",
  "machine_collection",
  "machine_refill",
  "machine_pickup_inventory",
  "machine_refund",
  "machine_clean",
  "machine_repair",
  "machine_install",
  "machine_remove",
];

export async function fetchCollectionTaskPredecessors(
  taskIds: string[],
): Promise<Record<string, CollectionTaskPredecessor>> {
  const ids = Array.from(new Set(taskIds.filter(Boolean))).sort();
  if (!ids.length) return {};
  const response = await fetchJson<Record<string, unknown>>(
    `${taskBase}/bulk/collection-predecessors`,
    {
      method: "POST",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      body: JSON.stringify({ ids }),
    },
    "Failed to load previous collections",
  );
  return normalizeCollectionTaskPredecessors(response);
}

export type TaskAuditEvent = {
  id: string;
  action: string;
  note: string;
  changes: Array<{
    field: string;
    label: string;
    before?: string | null;
    after?: string | null;
  }>;
  actor?: { id: string; name: string; email?: string | null } | null;
  createdAt: string;
};

export async function fetchTaskAuditEvents(
  taskId: string,
): Promise<TaskAuditEvent[]> {
  const response = await fetchJson<any[]>(
    `${taskBase}/${taskId}/audit-events`,
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to load task history",
  );
  return (Array.isArray(response) ? response : []).map((event) => ({
    id: String(event.id),
    action: String(event.action || ""),
    note: String(event.note || ""),
    changes: Array.isArray(event.changes) ? event.changes : [],
    actor: event.actor || null,
    createdAt: String(event.created_at ?? event.createdAt ?? ""),
  }));
}

export async function setCollectionCommissionDeferral(params: {
  taskId: string;
  deferred: boolean;
  note?: string;
}): Promise<Task> {
  const response = await fetchJson<any>(
    buildUrl(`${taskBase}/machine/machine_collection/commission/deferral`, {
      id: params.taskId,
    }),
    {
      method: "PUT",
      credentials: "include",
      headers: {
        ...(await defaultFetchHeaders()),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        deferred: params.deferred,
        note: params.note?.trim() || "",
      }),
    },
    "Failed to update commission deferral",
  );
  resetTasksCache();
  return normalizeTaskResponse(response);
}

export type CollectionCorrectionValues = {
  grossCashCents: number;
  grossCardCents: number;
  refundsCents: number;
  commissionCents: number;
  cardTransactionSurchargeTotalCents: number;
};

export async function correctCollectionTask(taskId: string, data: {
  expected: CollectionCorrectionValues;
  values: CollectionCorrectionValues;
  reason: string;
}): Promise<Task> {
  const response = await fetchJson<Parameters<typeof normalizeTaskResponse>[0]>(
    `${taskBase}/machine/machine_collection/${taskId}/correct`,
    { method: "POST", credentials: "include", headers: await defaultFetchHeaders(), body: JSON.stringify(data) },
    "Failed to correct collection",
  );
  resetTasksCache();
  return normalizeTaskResponse(response);
}

export async function fetchCollectionCommissionPayout(
  taskId: string,
): Promise<import("../../types").CommissionPayoutDetail> {
  const response = await fetchJson<Record<string, any>>(
    buildUrl(`${taskBase}/machine/machine_collection/commission/payout`, {
      id: taskId,
    }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to load commission payout",
  );
  return normalizeCommissionPayoutDetail(response);
}
const LIST_MACHINE_TASKS_URL = `${taskBase}/machine/list`;
const LIST_CONTACT_MACHINE_TASKS_URL = `${taskBase}/machine/contact/list`;
const PAGE_MACHINE_TASKS_URL = `${taskBase}/machine/page`;
const PAGE_CONTACT_MACHINE_TASKS_URL = `${taskBase}/machine/contact/page`;
const ROUTE_TASKS_URL = `${taskBase}/routes`;
const DASHBOARD_MACHINE_TASKS_URL = `${taskBase}/machine/dashboard/list`;
const REPORT_PAGE_MACHINE_TASKS_URL = `${taskBase}/machine/report/page`;
const REPORT_PAGE_CONTACT_MACHINE_TASKS_URL =
  `${taskBase}/machine/contact/report/page`;
const TASK_PAGE_SIZE = 250;
const REPORT_TASK_PAGE_SIZE = 1000;
const REPORT_TASK_MAX_RANGE_DAYS = 366;
let compatibleReportTaskPageSize = REPORT_TASK_PAGE_SIZE;

const usesCompactReportResponse = (filters?: TaskListFilters) =>
  filters?.responseMode === "report";

const TASKS_CACHE_TTL_MS = 30_000;
const tasksCache = createMemoryCache<Task[]>();
type TasksInFlightRequest = {
  version: string;
  promise: Promise<Task[]>;
};
const tasksInFlightOrg = new Map<string, TasksInFlightRequest>();
const tasksInFlightContact = new Map<string, TasksInFlightRequest>();
let tasksGlobalVersion = 0;
const taskCacheVersions = new Map<string, number>();

export type RefillInventorySuggestionLine = {
  itemId: string;
  product: Product;
  currentStock: number;
  capacity: number;
  suggestedRefill: number;
  sourceAvailableStock?: number | null;
  missingQuantity?: number;
  warehouseAvailableStock?: number | null;
  pickedUpQuantity?: number | null;
};

export type RefillInventorySuggestions = {
  taskId: string;
  machineId: string;
  sourceMode?: Task["inventorySourceMode"];
  vanId?: string | null;
  vanName?: string | null;
  warehouseId?: string | null;
  warehouseName?: string | null;
  warehouseAddress?: Address | null;
  items: RefillInventorySuggestionLine[];
};

export type RefillInventoryCompletionLineInput = {
  itemId: string;
  refilledQuantity: number;
  finalStock: number;
};

export type CollectionBackfillInput = {
  commissionAlreadyPaid?: boolean;
  machineId: string;
  assigneeId: string;
  collectionDate: string;
  doneAt?: string | null;
  grossCents: number;
  grossCashCents: number;
  grossCardCents: number;
  refundsCents: number;
  commissionCents: number;
  commissionPaymentType?: Task["commissionPaymentType"];
  cardTransactionSurchargeTotalCents?: number;
  distance?: number | null;
  durationSeconds?: number | null;
  notes?: string | null;
  assignmentConfirmed?: boolean;
};

export type CollectionBackfillError = {
  index: number;
  field: string;
  message: string;
};

export class CollectionBackfillApiError extends Error {
  errors: CollectionBackfillError[];

  constructor(errors: CollectionBackfillError[]) {
    super(errors[0]?.message || "Failed to backfill collections");
    this.name = "CollectionBackfillApiError";
    this.errors = errors;
  }
}

const numberOrZero = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const bumpTasksEpoch = () => {
  tasksGlobalVersion += 1;
};

export const getTasksEpoch = () => tasksGlobalVersion;

const getOrgCacheKey = () => {
  return `${getTaskIdentityKey()}:all`;
};

const taskFilterParams = (filters?: TaskListFilters) => ({
  machine_id: filters?.machineId,
  location_id: filters?.locationId,
  scheduled_from: filters?.scheduledFrom,
  scheduled_to: filters?.scheduledTo,
  overdue_to: filters?.overdueTo,
  include_visit_metrics:
    filters?.overdueTo
      ? undefined
      : filters?.includeVisitMetrics === false
        ? "false"
        : filters?.includeVisitMetrics === true
          ? "true"
          : undefined,
  status: filters?.statuses?.join(","),
  types: filters?.types?.join(","),
  query: filters?.query?.trim() || undefined,
  task_ids: filters?.taskIds?.filter(Boolean).join(",") || undefined,
  refill_task_ids: filters?.refillTaskIds?.filter(Boolean).join(",") || undefined,
});

export type TaskPage = {
  items: Task[];
  nextCursor: string | null;
  totalCount: number;
};

export async function fetchTaskPage(options?: {
  filters?: TaskListFilters;
  cursor?: string | null;
  limit?: number;
  responseMode?: "list" | "table";
  signal?: AbortSignal;
}): Promise<TaskPage> {
  const isContact = getEffectiveVendViewMode() === "contact";
  const response = await fetchJson<any>(
    withServerTiming(
      buildUrl(
        isContact ? PAGE_CONTACT_MACHINE_TASKS_URL : PAGE_MACHINE_TASKS_URL,
        {
          ...taskFilterParams(options?.filters),
          cursor: options?.cursor || undefined,
          limit: options?.limit || TASK_PAGE_SIZE,
          response_mode: options?.responseMode,
        },
      ),
      taskPerfEnabled(),
    ),
    {
      method: "GET",
      credentials: "include",
      headers: defaultReadHeaders(),
      signal: options?.signal,
    },
    "Failed to fetch tasks",
  );
  return {
    items: (Array.isArray(response?.items) ? response.items : []).map((task: any) =>
      normalizeTaskResponse(task),
    ),
    nextCursor: response?.nextCursor ?? response?.next_cursor ?? null,
    totalCount: Number(response?.totalCount ?? response?.total_count ?? 0),
  };
}

const hasBoundedTaskScope = (filters?: TaskListFilters) =>
  Boolean(
    filters?.machineId ||
      filters?.locationId ||
      (filters?.scheduledFrom && filters?.scheduledTo) ||
      filters?.taskIds?.some(Boolean) ||
      filters?.refillTaskIds?.some(Boolean)
  );

const operationalDateSpanDays = (filters?: TaskListFilters) => {
  if (!filters?.scheduledFrom || !filters.scheduledTo) return null;
  const start = Date.parse(`${filters.scheduledFrom}T00:00:00Z`);
  const end = Date.parse(`${filters.scheduledTo}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.floor((end - start) / 86_400_000) + 1;
};

const shouldPageTasks = (
  filters: TaskListFilters | undefined,
  compactReport: boolean,
) => {
  if (compactReport || !hasBoundedTaskScope(filters)) return true;
  const dateSpanDays = operationalDateSpanDays(filters);
  if (dateSpanDays !== null && dateSpanDays > 31) return true;
  return Boolean(
    (filters?.machineId || filters?.locationId) &&
      (!filters.scheduledFrom || !filters.scheduledTo)
  );
};

export const taskResponseIncludesVisitMetrics = (
  filters?: TaskListFilters,
) => {
  if (filters?.overdueTo) return false;
  if (filters?.includeVisitMetrics !== undefined) {
    return filters.includeVisitMetrics;
  }
  return !shouldPageTasks(filters, usesCompactReportResponse(filters));
};

async function fetchAllTaskPages(
  url: string,
  filters: TaskListFilters | undefined,
  pageSize = TASK_PAGE_SIZE,
): Promise<any[]> {
  const items: any[] = [];
  if (filters?.responseMode === "report" &&
      (operationalDateSpanDays(filters) || 0) > REPORT_TASK_MAX_RANGE_DAYS) {
    let start = filters.scheduledFrom!;
    const end = filters.scheduledTo!;
    while (start <= end) {
      const windowEnd = addDaysToDateKey(start, REPORT_TASK_MAX_RANGE_DAYS - 1);
      const through = windowEnd < end ? windowEnd : end;
      items.push(...await fetchAllTaskPages(url, {
        ...filters, scheduledFrom: start, scheduledTo: through,
      }, pageSize));
      start = addDaysToDateKey(through, 1);
    }
    return items;
  }
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  const effectivePageSize = pageSize;
  do {
    let page: any;
    try {
      page = await fetchJson<any>(
        withServerTiming(
          buildUrl(url, {
            ...taskFilterParams(filters),
            cursor,
            limit: effectivePageSize,
          }),
          taskPerfEnabled(),
        ),
        {
          method: "GET",
          credentials: "include",
          headers: defaultReadHeaders(),
        },
        "Failed to fetch tasks"
      );
    } catch (error) {
      const olderServerPageLimit =
        effectivePageSize > TASK_PAGE_SIZE &&
        error instanceof ApiResponseError &&
        error.status === 400 &&
        /limit must be between 1 and 250/i.test(error.message);
      if (!olderServerPageLimit) throw error;
      compatibleReportTaskPageSize = TASK_PAGE_SIZE;
      return fetchAllTaskPages(url, filters, TASK_PAGE_SIZE);
    }
    if (Array.isArray(page)) {
      if (cursor) throw new Error("Task data could not be loaded completely. Please retry.");
      return page;
    }
    if (!Array.isArray(page?.items) || !("nextCursor" in page || "next_cursor" in page)) {
      throw new Error("Task data could not be loaded completely. Please retry.");
    }
    items.push(...page.items);
    const nextCursor = page.nextCursor ?? page.next_cursor;
    if (nextCursor == null || nextCursor === "") break;
    if (typeof nextCursor !== "string" || seenCursors.has(nextCursor)) {
      throw new Error("Task data could not be loaded completely. Please retry.");
    }
    seenCursors.add(nextCursor);
    cursor = nextCursor;
  } while (cursor);
  return items;
}

const getTaskCacheKey = (filters?: TaskListFilters) => {
  const params = taskFilterParams(filters);
  const suffix = Object.entries(params)
    .filter(([, value]) => Boolean(value))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
  const mode = usesCompactReportResponse(filters) ? "report" : "full";
  const base = `${getOrgCacheKey()}:${mode}`;
  return suffix ? `${base}?${suffix}` : base;
};

const getContactTaskCacheKey = (filters?: TaskListFilters) => {
  const params = taskFilterParams(filters);
  const suffix = Object.entries(params)
    .filter(([, value]) => Boolean(value))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => `${key}:${value}`)
    .join("|");
  const mode = usesCompactReportResponse(filters) ? "report" : "full";
  const base = `${getTaskIdentityKey()}:contact:${mode}`;
  return suffix ? `${base}?${suffix}` : base;
};

const cacheTasks = (tasks: Task[], filters?: TaskListFilters) => {
  tasksCache.set(getTaskCacheKey(filters), tasks);
};

const clearTasksCache = () => {
  tasksCache.clear();
};

const cacheVersion = (key: string) =>
  `${tasksGlobalVersion}:${taskCacheVersions.get(key) || 0}`;

export const getTasksCacheVersion = (options?: {
  contact?: boolean;
  filters?: TaskListFilters;
}) =>
  cacheVersion(
    options?.contact
      ? getContactTaskCacheKey(options.filters)
      : getTaskCacheKey(options?.filters)
  );

const invalidateTaskCacheKey = (key: string) => {
  tasksCache.clear(key);
  taskCacheVersions.set(key, (taskCacheVersions.get(key) || 0) + 1);
};

export const invalidateTasksCache = (filters?: TaskListFilters[]) => {
  if (!filters?.length) {
    resetTasksCache();
    return;
  }
  filters.forEach((filter) => {
    invalidateTaskCacheKey(getTaskCacheKey(filter));
    invalidateTaskCacheKey(getContactTaskCacheKey(filter));
  });
};

export const resetTasksCache = () => {
  clearTasksCache();
  bumpTasksEpoch();
};

export async function fetchTasks(options?: {
  force?: boolean;
  filters?: TaskListFilters;
}): Promise<Task[]> {
  const orgCacheKey = getTaskCacheKey(options?.filters);
  const currentVersion = cacheVersion(orgCacheKey);
  const inFlight = tasksInFlightOrg.get(orgCacheKey);
  if (inFlight) {
    return inFlight.promise;
  }
  if (!options?.force) {
    const cached = tasksCache.get(orgCacheKey, TASKS_CACHE_TTL_MS);
    if (cached) return cached;
  }
  const requestVersion = currentVersion;
  const request = (async () => {
    const compactReport = usesCompactReportResponse(options?.filters);
    const paged =
      options?.filters?.responseMode === "report" ||
      shouldPageTasks(options?.filters, compactReport);
    const routable = usesRoutableTaskFeed(options?.filters);
    const url = routable
      ? ROUTE_TASKS_URL
      : options?.filters?.overdueTo
      ? DASHBOARD_MACHINE_TASKS_URL
      : compactReport
        ? REPORT_PAGE_MACHINE_TASKS_URL
        : paged
          ? PAGE_MACHINE_TASKS_URL
          : LIST_MACHINE_TASKS_URL;
    const taskRequest = (routable || paged) && !options?.filters?.overdueTo
      ? fetchAllTaskPages(
          url,
          options?.filters,
          compactReport ? compatibleReportTaskPageSize : TASK_PAGE_SIZE,
        )
      : fetchJson<any[]>(
          withServerTiming(
            buildUrl(url, taskFilterParams(options?.filters)),
            taskPerfEnabled(),
          ),
          {
            method: "GET",
            credentials: "include",
            headers: defaultReadHeaders(),
          },
          "Failed to fetch tasks"
        );
    const results = await taskRequest;
    const merged = results.map((task) =>
      normalizeTaskResponse(task, {
        partial: compactReport || Boolean(options?.filters?.overdueTo),
      })
    );
    const byId = new Map<string, Task>();
    merged.forEach((task) => {
      byId.set(task.id, task);
    });
    const list = Array.from(byId.values());
    if (requestVersion === cacheVersion(orgCacheKey)) {
      cacheTasks(list, options?.filters);
    }
    return list;
  })();
  tasksInFlightOrg.set(orgCacheKey, {
    version: requestVersion,
    promise: request,
  });
  try {
    return await request;
  } finally {
    if (tasksInFlightOrg.get(orgCacheKey)?.promise === request) {
      tasksInFlightOrg.delete(orgCacheKey);
    }
  }
}

export async function fetchContactTasks(
  options?: { force?: boolean; filters?: TaskListFilters }
): Promise<Task[]> {
  const contactCacheKey = getContactTaskCacheKey(options?.filters);
  const currentVersion = cacheVersion(contactCacheKey);
  const inFlightContact = tasksInFlightContact.get(contactCacheKey);
  if (inFlightContact) {
    return inFlightContact.promise;
  }
  if (!options?.force) {
    const cached = tasksCache.get(contactCacheKey, TASKS_CACHE_TTL_MS);
    if (cached) return cached;
  }
  const requestVersion = currentVersion;
  const request = (async () => {
    const compactReport = usesCompactReportResponse(options?.filters);
    const paged = shouldPageTasks(options?.filters, compactReport);
    const url = compactReport
      ? REPORT_PAGE_CONTACT_MACHINE_TASKS_URL
      : paged
        ? PAGE_CONTACT_MACHINE_TASKS_URL
        : LIST_CONTACT_MACHINE_TASKS_URL;
    const results = paged
      ? await fetchAllTaskPages(
          url,
          options?.filters,
          compactReport ? compatibleReportTaskPageSize : TASK_PAGE_SIZE,
        )
      : await fetchJson<any[]>(
          withServerTiming(
            buildUrl(url, taskFilterParams(options?.filters)),
            taskPerfEnabled(),
          ),
          {
            method: "GET",
            credentials: "include",
            headers: await defaultFetchHeaders(),
          },
          "Failed to fetch tasks"
        );
    const merged = results.map((task) =>
      normalizeTaskResponse(task, {
        partial: usesCompactReportResponse(options?.filters),
      })
    );
    const byId = new Map<string, Task>();
    merged.forEach((task) => {
      byId.set(task.id, task);
    });
    const list = Array.from(byId.values());
    if (requestVersion === cacheVersion(contactCacheKey)) {
      tasksCache.set(contactCacheKey, list);
    }
    return list;
  })();
  tasksInFlightContact.set(contactCacheKey, {
    version: requestVersion,
    promise: request,
  });
  try {
    return await request;
  } finally {
    if (tasksInFlightContact.get(contactCacheKey)?.promise === request) {
      tasksInFlightContact.delete(contactCacheKey);
    }
  }
}

function taskTypePath(type: TaskType) {
  if (!MACHINE_TASK_TYPES.includes(type)) {
    throw new Error(`Unsupported task type: ${type}`);
  }
  return `${taskBase}/machine/${type}`;
}

function contactTaskTypePath(type: TaskType) {
  if (!MACHINE_TASK_TYPES.includes(type)) {
    throw new Error(`Unsupported task type: ${type}`);
  }
  return `${taskBase}/machine/contact/${type}`;
}

export async function fetchTaskByType(
  type: TaskType,
  id: string
): Promise<Task> {
  const isContactView = getEffectiveVendViewMode() === "contact";
  const task = await fetchJson<any>(
    buildUrl(isContactView ? contactTaskTypePath(type) : taskTypePath(type), { id }),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch task"
  );
  return normalizeTaskResponse(task);
}

export async function fetchTaskById(id: string): Promise<Task> {
  const isContactView = getEffectiveVendViewMode() === "contact";
  const task = await fetchJson<any>(
    buildUrl(isContactView ? `${taskBase}/contact` : taskBase, { id }),
    {
      method: "GET",
      credentials: "include",
      headers: await defaultFetchHeaders(),
    },
    "Failed to fetch task"
  );
  return normalizeTaskResponse(task);
}

export async function fetchRefillInventorySuggestions(
  taskId: string,
  warehouseId?: string | null,
  vanId?: string | null,
  signal?: AbortSignal,
): Promise<RefillInventorySuggestions> {
  const response = await fetchJson<any>(
    buildUrl(`${taskBase}/machine/machine_refill/inventory/suggestions`, {
      id: taskId,
      warehouseId: warehouseId || undefined,
      vanId: vanId || undefined,
    }),
    {
      method: "GET",
      credentials: "include",
      headers: defaultReadHeaders(),
      signal,
    },
    "Failed to fetch refill inventory suggestions",
  );
  return {
    taskId: String(response?.taskId ?? response?.task_id ?? taskId),
    machineId: String(response?.machineId ?? response?.machine_id ?? ""),
    sourceMode: response?.sourceMode ?? response?.source_mode ?? "warehouse",
    vanId: response?.vanId ?? response?.van_id ?? null,
    vanName: response?.vanName ?? response?.van_name ?? null,
    warehouseId: response?.warehouseId ?? response?.warehouse_id ?? null,
    warehouseName: response?.warehouseName ?? response?.warehouse_name ?? null,
    warehouseAddress: response?.warehouseAddress ?? response?.warehouse_address ?? null,
    items: (Array.isArray(response?.items) ? response.items : []).map((line: any) => ({
      itemId: String(line?.itemId ?? line?.item_id ?? ""),
      product: normalizeProduct(line?.product || {}),
      currentStock: numberOrZero(line?.currentStock ?? line?.current_stock),
      capacity: numberOrZero(line?.capacity),
      suggestedRefill: numberOrZero(line?.suggestedRefill ?? line?.suggested_refill),
      sourceAvailableStock:
        line?.sourceAvailableStock == null && line?.source_available_stock == null
          ? null
          : numberOrZero(line?.sourceAvailableStock ?? line?.source_available_stock),
      missingQuantity: numberOrZero(line?.missingQuantity ?? line?.missing_quantity),
      warehouseAvailableStock:
        line?.warehouseAvailableStock == null && line?.warehouse_available_stock == null
          ? null
          : numberOrZero(line?.warehouseAvailableStock ?? line?.warehouse_available_stock),
      pickedUpQuantity:
        line?.pickedUpQuantity == null && line?.picked_up_quantity == null
          ? null
          : numberOrZero(line?.pickedUpQuantity ?? line?.picked_up_quantity),
    })),
  };
}

export async function setRefillInventorySource(
  taskId: string,
  warehouseId: string | null,
  sourceMode: NonNullable<Task["inventorySourceMode"]> = warehouseId ? "warehouse" : "untracked",
  vanId: string | null = null,
): Promise<Task> {
  const task = await fetchJson<any>(
    buildUrl(`${taskBase}/machine/machine_refill/inventory/source`, { id: taskId }),
    {
      method: "PUT",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      body: JSON.stringify({ sourceMode, warehouseId, vanId }),
    },
    "Failed to set refill inventory source",
  );
  return normalizeTaskResponse(task);
}

export async function createRefillInventoryPickup(taskId: string, warehouseId: string, vanId: string | null = null): Promise<Task> {
  const identity = getTaskIdentityKey();
  const scopeHeaders = defaultReadHeaders();
  const headers = await defaultFetchHeaders();
  if (identity !== getTaskIdentityKey()) throw new Error("Account changed. Try again.");
  const task = await fetchJson<any>(
    buildUrl(`${taskBase}/machine/machine_refill/inventory/pickup`, { id: taskId }),
    { method: "POST", credentials: "include", headers: { ...headers, ...scopeHeaders }, body: JSON.stringify({ warehouseId, vanId }) },
    "Failed to create warehouse pickup",
  );
  return normalizeTaskResponse(task);
}

export async function completeRefillInventoryTask(
  taskId: string,
  items: RefillInventoryCompletionLineInput[],
): Promise<Task> {
  const task = await fetchJson<any>(
    buildUrl(`${taskBase}/machine/machine_refill/complete`, { id: taskId }),
    {
      method: "POST",
      credentials: "include",
      headers: await defaultFetchHeaders(),
      body: JSON.stringify({ items }),
    },
    "Failed to complete refill",
  );
  return normalizeTaskResponse(task);
}

export async function uploadTaskConfirmationPhoto(
  taskId: string,
  file: File,
  scope?: "growth",
): Promise<void> {
  const form = new FormData();
  form.append("file", file);
  const token = getCsrfToken() || (await setCsrfToken());
  const headers = await defaultFetchHeaders();
  delete (headers as Record<string, string>)["Content-Type"];
  const res = await fetch(
    scope === "growth"
      ? `${backend}/growth/tasks/${encodeURIComponent(taskId)}/photo`
      : buildUrl(`${taskBase}/asset/confirmation_photo/add`, { task_id: taskId }),
    {
      method: "POST",
      credentials: "include",
      headers: {
        ...headers,
        "X-CSRFToken": token,
      },
      body: form,
    }
  );
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to upload photo")
    );
  }
}

export async function deleteTaskAsset(assetId: string, growthTaskId?: string): Promise<void> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(growthTaskId
    ? `${backend}/growth/tasks/${encodeURIComponent(growthTaskId)}/photo/${encodeURIComponent(assetId)}`
    : buildUrl(`${taskBase}/asset/delete`, { id: assetId }), {
    method: "DELETE",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "X-CSRFToken": token,
    },
  });
  if (!res.ok) {
    throw new Error(
      await errorMessageFromResponse(res, "Failed to remove photo")
    );
  }
}

export async function fetchServiceFormMachine(
  machineId: string
): Promise<{
  machineId: string;
  locationName?: string | null;
  organizationTitle?: string | null;
}> {
  const res = await fetchJson<any>(
    buildUrl(`${taskBase}/service-form/machine`, { machine_id: machineId }),
    {
      method: "GET",
      headers : await defaultFetchHeaders(),
    },
    "Failed to fetch machine"
  );
  return {
    machineId: res.machineId ?? res.machine_id ?? "",
    locationName: res.locationName ?? res.location_name ?? null,
    organizationTitle: res.organizationTitle ?? res.organization_title ?? null,
  };
}

export async function submitServiceForm(payload: {
  machineId: string;
  serviceType: TaskType;
  scheduledFor?: string;
  requesterEmail?: string;
  notes?: string;
  refundAmount?: number;
  captchaToken: string;
}): Promise<void> {
  const body = {
    machineId: payload.machineId,
    serviceType: payload.serviceType,
    scheduledFor: payload.scheduledFor ?? undefined,
    requesterEmail: payload.requesterEmail ?? undefined,
    notes: payload.notes ?? undefined,
    refundAmount: payload.refundAmount ?? undefined,
    captcha_token: payload.captchaToken,
  };
  await fetchJson(
    `${taskBase}/service-form/submit`,
    {
      method: "POST",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Failed to submit service request"
  );
}

export async function createTask(payload: {
  type: TaskType;
  machineId: string;
  locationId?: string;
  notes?: string;
  scheduledFor?: string;
  isPublic?: boolean;
  assigneeId?: string | null;
  grossCents?: number | null;
  grossCashCents?: number | null;
  grossCardCents?: number | null;
  refundsCents?: number | null;
  commissionCents?: number | null;
  commissionPaymentType?: Task["commissionPaymentType"];
  netCents?: number | null;
  includeRefundsInCommission?: boolean | null;
  childTaskTypes?: TaskType[];
}): Promise<Task> {
  const scheduledFor = payload.scheduledFor || toLocalISODate(new Date());
  const createdAt = new Date().toISOString();
  const body: Record<string, unknown> = {
    type: mapTaskTypeToApi(payload.type),
    status: payload.assigneeId ? "pending" : "unassigned",
    machine: payload.machineId,
    notes: payload.notes ?? null,
    created_at: createdAt,
    scheduled_for: scheduledFor,
    is_public: payload.isPublic ?? false,
    assignee: payload.assigneeId ?? undefined,
  };
  if (payload.type === "machine_service" && payload.childTaskTypes) {
    body.childTaskTypes = payload.childTaskTypes;
  }
  if (payload.type === "machine_collection") {
    body.grossCents = payload.grossCents ?? 0.0;
    if (payload.grossCashCents !== undefined) body.grossCashCents = payload.grossCashCents;
    if (payload.grossCardCents !== undefined) body.grossCardCents = payload.grossCardCents;
    body.refundsCents = payload.refundsCents ?? 0.0;
    body.commissionCents = payload.commissionCents ?? 0.0;
    if (payload.commissionPaymentType !== undefined) {
      body.commissionPaymentType = payload.commissionPaymentType;
    }
    body.netCents = payload.netCents ?? 0.0;
    body.includeRefundsInCommission =
      payload.includeRefundsInCommission ?? null;
  }
  if (payload.type === "machine_refund") {
    body.refundsCents = payload.refundsCents ?? 0.0;
  }
  const created = await fetchJson<any>(
    `${taskTypePath(payload.type)}/create`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Failed to create task"
  );
  return normalizeTaskResponse(created);
}

export async function bulkCreateTasks(
  items: Array<{
    type: TaskType;
    machineId: string;
    locationId?: string;
    notes?: string;
    scheduledFor?: string;
    isPublic?: boolean;
    assigneeId?: string | null;
    grossCents?: number | null;
    grossCashCents?: number | null;
    grossCardCents?: number | null;
    refundsCents?: number | null;
    commissionCents?: number | null;
    commissionPaymentType?: Task["commissionPaymentType"];
    netCents?: number | null;
    includeRefundsInCommission?: boolean | null;
    childTaskTypes?: TaskType[];
  }>
): Promise<Task[]> {
  if (!items.length) return [];
  const createdAt = new Date().toISOString();
  const body = {
    items: items.map((payload) => {
      const scheduledFor = payload.scheduledFor || toLocalISODate(new Date());
      const item: Record<string, unknown> = {
        type: mapTaskTypeToApi(payload.type),
        status: payload.assigneeId ? "pending" : "unassigned",
        machine: payload.machineId,
        notes: payload.notes ?? null,
        created_at: createdAt,
        scheduled_for: scheduledFor,
        is_public: payload.isPublic ?? false,
        assignee: payload.assigneeId ?? undefined,
      };
      if (payload.type === "machine_collection") {
        item.grossCents = payload.grossCents ?? 0.0;
        if (payload.grossCashCents !== undefined) item.grossCashCents = payload.grossCashCents;
        if (payload.grossCardCents !== undefined) item.grossCardCents = payload.grossCardCents;
        item.refundsCents = payload.refundsCents ?? 0.0;
        item.commissionCents = payload.commissionCents ?? 0.0;
        if (payload.commissionPaymentType !== undefined) {
          item.commissionPaymentType = payload.commissionPaymentType;
        }
        item.netCents = payload.netCents ?? 0.0;
        item.includeRefundsInCommission =
          payload.includeRefundsInCommission ?? null;
      }
      if (payload.type === "machine_refund") {
        item.refundsCents = payload.refundsCents ?? 0.0;
      }
      if (payload.type === "machine_service" && payload.childTaskTypes) {
        item.childTaskTypes = payload.childTaskTypes;
      }
      return item;
    }),
  };
  const created = await fetchJson<any[]>(
    `${taskBase}/bulk/create`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
    "Failed to create tasks"
  );
  return created.map((task) => normalizeTaskResponse(task));
}

export async function backfillCollectionTasks(payload: {
  locationId: string;
  items: CollectionBackfillInput[];
}): Promise<Task[]> {
  const response = await fetch(`${taskBase}/machine/machine_collection/backfill`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({
      locationId: payload.locationId,
      items: payload.items.map((item) => ({
        machine: item.machineId,
        assignee: item.assigneeId,
        collectionDate: item.collectionDate,
        doneAt: item.doneAt || null,
        grossCents: item.grossCents,
        grossCashCents: item.grossCashCents,
        grossCardCents: item.grossCardCents,
        refundsCents: item.refundsCents,
        commissionCents: item.commissionCents,
        commissionPaymentType: item.commissionPaymentType || null,
        commissionAlreadyPaid: item.commissionAlreadyPaid ?? true,
        cardTransactionSurchargeTotalCents: item.cardTransactionSurchargeTotalCents ?? 0,
        distance: item.distance ?? null,
        durationSeconds: item.durationSeconds ?? null,
        notes: item.notes || null,
        assignmentConfirmed: item.assignmentConfirmed ?? false,
      })),
    }),
  });
  if (!response.ok) {
    let parsed: any = null;
    try {
      parsed = await response.json();
    } catch {
      // fall through to text fallback
    }
    if (Array.isArray(parsed?.errors)) {
      throw new CollectionBackfillApiError(parsed.errors);
    }
    throw new Error(parsed?.detail || parsed?.message || "Failed to backfill collections");
  }
  const created = await jsonFromResponse<any[]>(
    response,
    "Failed to backfill collections"
  );
  return (created || []).map((task: any) => normalizeTaskResponse(task));
}

export async function precheckTask(payload: {
  type: TaskType;
  machineId: string;
  scheduledFor?: string;
}): Promise<TaskPrecheckResponse> {
  const scheduledFor = payload.scheduledFor || toLocalISODate(new Date());
  const response = await fetchJson<any>(
    buildUrl(`${taskTypePath(payload.type)}/precheck`, {
      mid: payload.machineId,
      scheduled_for: scheduledFor,
    }),
    {
      method: "GET",
      credentials: "include",
      headers : await defaultFetchHeaders(),
    },
    "Failed to precheck task"
  );
  return {
    ok: response.ok,
    reason: response.reason ?? null,
    existingTask: response.existing_task
      ? {
          id: response.existing_task.id,
          type: response.existing_task.type,
          scheduledFor: response.existing_task.scheduled_for,
          locationId: response.existing_task.location_id,
          machineId: response.existing_task.machine_id,
        }
      : null,
  };
}

export async function bulkPrecheckTasks(
  items: Array<{
    taskId?: string;
    type: TaskType;
    machineId: string;
    scheduledFor?: string;
  }>
): Promise<TaskPrecheckResponse[]> {
  if (!items.length) return [];
  const response = await fetchJson<any[]>(
    `${taskBase}/bulk/precheck`,
    {
      method: "POST",
      credentials: "include",
      headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
      body: JSON.stringify({
        items: items.map((item) => ({
          taskId: item.taskId,
          type: item.type,
          machineId: item.machineId,
          scheduledFor: item.scheduledFor || toLocalISODate(new Date()),
        })),
      }),
    },
    "Failed to precheck tasks"
  );
  return response.map((item) => ({
    ok: item.ok,
    reason: item.reason ?? null,
    existingTask: item.existingTask ?? item.existing_task
      ? {
          id: (item.existingTask ?? item.existing_task).id,
          type: (item.existingTask ?? item.existing_task).type,
          scheduledFor:
            (item.existingTask ?? item.existing_task).scheduledFor ??
            (item.existingTask ?? item.existing_task).scheduled_for,
          locationId:
            (item.existingTask ?? item.existing_task).locationId ??
            (item.existingTask ?? item.existing_task).location_id,
          machineId:
            (item.existingTask ?? item.existing_task).machineId ??
            (item.existingTask ?? item.existing_task).machine_id,
        }
      : null,
  }));
}

const mergeTaskUpdates = (
  current: Task,
  updates: Partial<{
    type: TaskType;
    status: TaskStatus;
    machineId: string | null;
    locationId: string | null;
    notes: string | null;
    scheduledFor: string | null;
    isPublic: boolean | null;
    assigneeId: string | null;
    grossCents: number | null;
    grossCashCents: number | null;
    grossCardCents: number | null;
    refundsCents: number | null;
    commissionCents: number | null;
    commissionPaymentType: Task["commissionPaymentType"];
    netCents: number | null;
    includeRefundsInCommission: boolean | null;
    prefill: boolean | null;
  }>
): Task => {
  const next: Task = { ...current };
  if ("type" in updates && updates.type) next.type = updates.type;
  if ("status" in updates && updates.status !== undefined) {
    next.status = updates.status;
  }
  if ("machineId" in updates) next.machineId = updates.machineId ?? null;
  if ("locationId" in updates) next.locationId = updates.locationId ?? null;
  if ("notes" in updates) next.notes = updates.notes ?? undefined;
  if ("scheduledFor" in updates) next.scheduledFor = updates.scheduledFor ?? null;
  if ("isPublic" in updates) next.isPublic = updates.isPublic ?? undefined;
  if ("assigneeId" in updates) next.userId = updates.assigneeId ?? null;
  if ("grossCents" in updates) next.grossCents = updates.grossCents ?? null;
  if ("grossCashCents" in updates) next.grossCashCents = updates.grossCashCents ?? null;
  if ("grossCardCents" in updates) next.grossCardCents = updates.grossCardCents ?? null;
  if ("refundsCents" in updates) next.refundsCents = updates.refundsCents ?? null;
  if ("commissionCents" in updates) next.commissionCents = updates.commissionCents ?? null;
  if ("commissionPaymentType" in updates) {
    next.commissionPaymentType = updates.commissionPaymentType ?? null;
  }
  if ("netCents" in updates) next.netCents = updates.netCents ?? null;
  return next;
};

export async function editTask(
  id: string,
  type: TaskType,
  updates: Partial<{
    type: TaskType;
    status: TaskStatus;
    machineId: string | null;
    locationId: string | null;
    notes: string | null;
    scheduledFor: string | null;
    isPublic: boolean | null;
    assigneeId: string | null;
    grossCents: number | null;
    grossCashCents: number | null;
    grossCardCents: number | null;
    refundsCents: number | null;
    commissionCents: number | null;
    commissionPaymentType: Task["commissionPaymentType"];
    netCents: number | null;
    includeRefundsInCommission: boolean | null;
    prefill: boolean | null;
  }>,
  options?: { currentTask?: Task }
): Promise<Task> {
  const notes =
    updates.notes === null
      ? null
      : updates.notes === undefined
      ? undefined
      : updates.notes;
  const body = {
    type: mapTaskTypeToApi(updates.type),
    machine: updates.machineId === null ? null : updates.machineId ?? undefined,
    notes,
    scheduled_for:
      updates.scheduledFor === null ? null : updates.scheduledFor ?? undefined,
    is_public: updates.isPublic === null ? null : updates.isPublic ?? undefined,
    assignee:
      updates.assigneeId === null ? null : updates.assigneeId ?? undefined,
    grossCents: updates.grossCents === null ? null : updates.grossCents ?? undefined,
    grossCashCents:
      updates.grossCashCents === null ? null : updates.grossCashCents ?? undefined,
    grossCardCents:
      updates.grossCardCents === null ? null : updates.grossCardCents ?? undefined,
    refundsCents: updates.refundsCents === null ? null : updates.refundsCents ?? undefined,
    commissionCents:
      updates.commissionCents === null ? null : updates.commissionCents ?? undefined,
    commissionPaymentType:
      updates.commissionPaymentType === null
        ? null
        : updates.commissionPaymentType ?? undefined,
    netCents: updates.netCents === null ? null : updates.netCents ?? undefined,
    includeRefundsInCommission:
      updates.includeRefundsInCommission === null
        ? null
        : updates.includeRefundsInCommission ?? undefined,
    prefill: updates.prefill === null ? null : updates.prefill ?? undefined,
  };
  const res = await fetch(buildUrl(`${taskTypePath(type)}/edit`, { id }), {
    method: "PUT",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(res, "Failed to update task");
    }
    throw new Error(
      await errorMessageFromResponse(res, "Failed to update task")
    );
  }
  if (options?.currentTask) {
    return mergeTaskUpdates(options.currentTask, updates);
  }
  return fetchTaskByType(type, id);
}

export async function bulkEditTasks(
  ids: string[],
  updates: Partial<{
    type: TaskType;
    status: TaskStatus;
    machineId: string | null;
    locationId: string | null;
    notes: string | null;
    scheduledFor: string | null;
    isPublic: boolean | null;
    assigneeId: string | null;
    grossCents: number | null;
    grossCashCents: number | null;
    grossCardCents: number | null;
    refundsCents: number | null;
    commissionCents: number | null;
    commissionPaymentType: Task["commissionPaymentType"];
    netCents: number | null;
    includeRefundsInCommission: boolean | null;
    prefill: boolean | null;
  }>
): Promise<Task[]> {
  if (!ids.length) return [];
  const notes =
    updates.notes === null
      ? null
      : updates.notes === undefined
      ? undefined
      : updates.notes;
  const body = {
    ids,
    status:
      updates.status === null ? null : updates.status ?? undefined,
    scheduledFor:
      updates.scheduledFor === null ? null : updates.scheduledFor ?? undefined,
    assignee:
      updates.assigneeId === null ? null : updates.assigneeId ?? undefined,
    notes,
    grossCents: updates.grossCents === null ? null : updates.grossCents ?? undefined,
    grossCashCents:
      updates.grossCashCents === null ? null : updates.grossCashCents ?? undefined,
    grossCardCents:
      updates.grossCardCents === null ? null : updates.grossCardCents ?? undefined,
    refundsCents: updates.refundsCents === null ? null : updates.refundsCents ?? undefined,
    commissionCents:
      updates.commissionCents === null ? null : updates.commissionCents ?? undefined,
    commissionPaymentType:
      updates.commissionPaymentType === null
        ? null
        : updates.commissionPaymentType ?? undefined,
    netCents: updates.netCents === null ? null : updates.netCents ?? undefined,
    includeRefundsInCommission:
      updates.includeRefundsInCommission === null
        ? null
        : updates.includeRefundsInCommission ?? undefined,
    prefill: updates.prefill === null ? null : updates.prefill ?? undefined,
  };
  const res = await fetch(`${taskBase}/bulk/edit`, {
    method: "PUT",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(res, "Failed to update tasks");
    }
    throw new Error(await errorMessageFromResponse(res, "Failed to update tasks"));
  }
  const results = await jsonFromResponse<any[]>(res, "Failed to update tasks");
  return results.map((task) => normalizeTaskResponse(task));
}

export async function deleteTask(
  id: string,
  type: TaskType
): Promise<{ id: string }> {
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(buildUrl(`${taskTypePath(type)}/delete`, { id }), {
    method: "DELETE",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "X-CSRFToken": token
    },
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(res, "Failed to delete task");
    }
    throw new Error(
      await errorMessageFromResponse(res, "Failed to delete task")
    );
  }
  return { id };
}

export async function bulkDeleteTasks(ids: string[]): Promise<void> {
  if (!ids.length) return;
  const token = getCsrfToken() || (await setCsrfToken());
  const res = await fetch(`${taskBase}/bulk/delete`, {
    method: "DELETE",
    credentials: "include",
    headers: {
      ...(await defaultFetchHeaders()),
      "Content-Type": "application/json",
      "X-CSRFToken": token,
    },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(res, "Failed to delete tasks");
    }
    throw new Error(await errorMessageFromResponse(res, "Failed to delete tasks"));
  }
}

export async function bulkCancelTasks(ids: string[]): Promise<Task[]> {
  if (!ids.length) return [];
  const res = await fetch(`${taskBase}/bulk/cancel`, {
    method: "POST",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(res, "Failed to cancel tasks");
    }
    throw new Error(await errorMessageFromResponse(res, "Failed to cancel tasks"));
  }
  const results = await jsonFromResponse<any[]>(res, "Failed to cancel tasks");
  return results.map((task) => normalizeTaskResponse(task));
}

export async function updateTaskStatus(
  id: string,
  status: TaskStatus,
  type?: TaskType
): Promise<void> {
  const nextStatus = mapTaskStatusToApi(status);
  const query = new URLSearchParams();
  query.append("id", id);
  if (nextStatus) query.append("status", nextStatus);
  const endpoint =
    type && MACHINE_TASK_TYPES.includes(type)
      ? `${taskBase}/machine/${type}/status/update`
      : `${taskBase}/status/update`;
  const res = await fetch(`${endpoint}?${query.toString()}`, {
    method: "PUT",
    credentials: "include",
    headers : await defaultFetchHeaders(),
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(
        res,
        "Failed to update task status"
      );
    }
    throw new Error(
      await errorMessageFromResponse(res, "Failed to update task status")
    );
  }
}

export async function updateTaskStatuses(
  ids: string[],
  status: TaskStatus,
  options?: { cascadeBundle?: boolean }
): Promise<Task[]> {
  if (!ids.length) return [];
  const res = await fetch(`${taskBase}/bulk/status`, {
    method: "PUT",
    credentials: "include",
    headers: { ...(await defaultFetchHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify({
      ids,
      status: mapTaskStatusToApi(status),
      cascadeBundle: options?.cascadeBundle ?? true,
    }),
  });
  if (!res.ok) {
    if (res.status === 402) {
      throw await handlePaymentRequired(
        res,
        "Failed to update task statuses"
      );
    }
    throw new Error(
      await errorMessageFromResponse(res, "Failed to update task statuses")
    );
  }
  const results = await jsonFromResponse<any[]>(
    res,
    "Failed to update task statuses"
  );
  return results.map((task) => normalizeTaskResponse(task));
}

export async function fetchTask(id: string): Promise<Task> {
  return fetchTaskById(id);
}

export async function createServiceTask(payload: {
  type: TaskType;
  machineId: string;
  locationId?: string;
  notes?: string;
  scheduledFor?: string;
  isPublic?: boolean;
}): Promise<Task> {
  return createTask({ ...payload, type: "machine_service" });
}

export async function editServiceTask(
  id: string,
  updates: Partial<{
    type: TaskType;
    status: TaskStatus;
    machineId: string | null;
    locationId: string | null;
    notes: string | null;
    scheduledFor: string | null;
    isPublic: boolean | null;
  }>
): Promise<Task> {
  return editTask(id, "machine_service", updates);
}

export async function deleteServiceTask(id: string): Promise<{ id: string }> {
  return deleteTask(id, "machine_service");
}
